import React from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { BLOG_POSTS } from "../data/blogPosts";
import { ArrowRight, Clock, Calendar } from "lucide-react";
import SEOHead from "../components/SEOHead";

/* Inline renderer that supports:
   • Plain string paragraphs → <p>
   • { h2: "..." } and { h3: "..." } subheadings → <h2>/<h3>
   • Markdown-style internal links inside strings: [link text](/some-path)
     External http(s) links render as <a target="_blank" rel="noopener">.
   This unlocks per-post SEO internal linking back into high-converting pages. */
const LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;

function renderInline(text) {
 const out = [];
 let lastIdx = 0;
 let m;
 LINK_RE.lastIndex = 0;
 while ((m = LINK_RE.exec(text)) !== null) {
  if (m.index > lastIdx) out.push(text.slice(lastIdx, m.index));
  const [, label, href] = m;
  const isExternal = /^https?:\/\//i.test(href);
  out.push(isExternal
   ? <a key={`l-${m.index}`} href={href} target="_blank" rel="noopener noreferrer" className="text-pink underline decoration-pink/40 hover:decoration-pink/90 underline-offset-2">{label}</a>
   : <Link key={`l-${m.index}`} to={href} className="text-pink underline decoration-pink/40 hover:decoration-pink/90 underline-offset-2">{label}</Link>);
  lastIdx = m.index + m[0].length;
 }
 if (lastIdx < text.length) out.push(text.slice(lastIdx));
 return out.length ? out : text;
}

function BlogBody({ body }) {
 return (
  <div className="mt-10 space-y-5 text-ink-soft leading-relaxed text-[17px]">
   {body.map((b, i) => {
    if (typeof b === "string") return <p key={i}>{renderInline(b)}</p>;
    if (b && b.h2) return <h2 key={i} className="font-display text-2xl lg:text-3xl text-ink mt-10 mb-2 leading-tight">{renderInline(b.h2)}</h2>;
    if (b && b.h3) return <h3 key={i} className="font-display text-xl text-ink mt-7 mb-2 leading-tight">{renderInline(b.h3)}</h3>;
    return null;
   })}
  </div>
 );
}

export function BlogIndex() {
 return (
 <main className="pt-32 pb-24" data-testid="blog-index">
 <SEOHead
  title="Sydney Harbour Cruise Blog | Event Guides, Tips & Stories | Magic Cruises"
  description="Sydney Harbour cruise planning guides, event tips and stories , birthday cruise ideas, wedding cruise planning, hens party guides and corporate event playbooks from Magic Cruises."
  image={BLOG_POSTS[0]?.cover}
  canonical="/blog"
  keywords="Sydney Harbour cruise blog, harbour event planning, wedding cruise guide, birthday cruise ideas, hens party cruise tips"
  breadcrumbs={[
   { name: "Home", url: "/" },
   { name: "Blog", url: "/blog" },
  ]}
 />
 <div className="mx-auto max-w-7xl px-5 lg:px-10">
 <div className="eyebrow">The Blog</div>
 <h1 className="font-display text-4xl lg:text-7xl mt-3 mb-10 text-ink leading-[1]">Sydney Harbour event <span className="text-pink italic">guides</span>.</h1>
 <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
 {BLOG_POSTS.map((p) => (
 <Link key={p.slug} to={`/blog/${p.slug}`} className="bg-white rounded-2xl border border-[hsla(222,20%,80%,0.5)] overflow-hidden group card-pop" data-testid={`blog-card-${p.slug}`}>
 <div className="aspect-[16/10] overflow-hidden border-b border-[hsla(222,20%,80%,0.5)]"><img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s]" /></div>
 <div className="p-6">
 <div className="flex items-center gap-4 text-xs text-ink-mute mb-3 font-bold uppercase tracking-wider"><span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" />{p.date}</span><span className="flex items-center gap-1.5"><Clock className="w-3 h-3" />{p.minutes} min</span></div>
 <h2 className="font-display text-xl mb-3 text-ink leading-tight">{p.title}</h2>
 <p className="text-ink-soft text-sm leading-relaxed">{p.excerpt}</p>
 <div className="mt-4 text-pink text-xs flex items-center gap-1 font-bold uppercase tracking-[0.18em]">Read <ArrowRight className="w-3 h-3" /></div>
 </div>
 </Link>
 ))}
 </div>
 </div>
 </main>
 );
}

export function BlogPost() {
 const { slug } = useParams();
 const navigate = useNavigate();
 const post = BLOG_POSTS.find(p => p.slug === slug);
 React.useEffect(() => {
 if (!post) { navigate("/blog"); }
 }, [post, navigate]);
 if (!post) return null;
 return (
 <main className="pt-32 pb-24" data-testid={`blog-post-${slug}`}>
 <SEOHead
  title={`${post.title} | Magic Cruises Sydney Harbour Blog`}
  description={post.excerpt}
  image={post.cover}
  type="Article"
  canonical={`/blog/${post.slug}`}
  breadcrumbs={[
   { name: "Home", url: "/" },
   { name: "Blog", url: "/blog" },
   { name: post.title, url: `/blog/${post.slug}` },
  ]}
 />
 <article className="mx-auto max-w-3xl px-5 lg:px-10">
 <Link to="/blog" className="text-pink text-xs uppercase tracking-widest font-bold hover:underline">← All posts</Link>
 <h1 className="font-display text-3xl lg:text-6xl mt-6 leading-[1.05] text-ink">{post.title}.</h1>
 <div className="flex items-center gap-4 text-xs text-ink-mute mt-4 font-bold uppercase tracking-wider"><span>{post.date}</span><span>·</span><span>{post.minutes} min read</span></div>
 <div className="plate aspect-[16/10] mt-8"><img src={post.cover} alt={post.title} className="absolute inset-0 w-full h-full object-cover" /></div>
<BlogBody body={post.body} />
 <div className="mt-12 bg-white rounded-2xl border border-[hsla(222,20%,80%,0.5)] p-8 text-center">
 <div className="font-display text-3xl mb-3 text-ink">Ready to book your cruise?</div>
 <Link to="/contact" className="btn-magic mt-2 inline-flex">Enquire Now <ArrowRight className="w-3.5 h-3.5" /></Link>
 </div>
 </article>
 </main>
 );
}
